import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, message } from "antd";
import thiranLogo from "../assets/thiran-logo.svg";
import api from "../lib/api";
import "../components/Demo/Feedback/KioskFeedbackForm.css";

const { Option } = Select;

const PLAY_STORE_URL =
  import.meta.env.VITE_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.thirantech.team";
const APP_STORE_URL =
  import.meta.env.VITE_APP_STORE_URL ||
  "https://apps.apple.com/in/app/t-eam/id6449088124";

const DemoEnrollmentForm: React.FC = () => {
  const [form] = Form.useForm();
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState<number>(5);
  const [detectedPlatform, setDetectedPlatform] = useState<
    "android" | "ios" | "web"
  >("web");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const detectPlatformFromUserAgent = (
    ua: string
  ): "android" | "ios" | "web" => {
    const uaLower = ua.toLowerCase();
    if (uaLower.includes("android")) return "android";
    if (
      uaLower.includes("iphone") ||
      uaLower.includes("ipad") ||
      uaLower.includes("ios")
    )
      return "ios";
    return "web";
  };

  useEffect(() => {
    const platform = detectPlatformFromUserAgent(navigator.userAgent);
    setDetectedPlatform(platform);
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const response = await api.post("/demo/registerForDemo", values);
      if (response.status === 201) {
        message.success("Thank you for registering!");
        setSubmitted(true);
      } else {
        message.error(response.data.message || "Something went wrong.");
      }
    } catch (error: any) {
      message.error(
        "Network error: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (submitted && detectedPlatform !== "web") {
      setCountdown(5); // Reset countdown on submit
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timer = setTimeout(() => {
        if (detectedPlatform === "android") {
          window.location.href = PLAY_STORE_URL;
        } else if (detectedPlatform === "ios") {
          window.location.href = APP_STORE_URL;
        }
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [submitted, detectedPlatform]);

  const renderStoreButtons = () => (
    <div className="flex flex-col gap-4 items-center mt-4">
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
          alt="Get it on Google Play"
          className="w-40"
        />
      </a>
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        <img
          src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
          alt="Download on the App Store"
          className="w-40"
        />
      </a>
    </div>
  );

  return (
    <div
      className="w-full min-h-[100dvh]"
      style={{
        backgroundImage:
          "linear-gradient(to bottom right, rgb(239 246 255), #a8ccde)",
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-[9999] p-1 bg-white bg-opacity-90 backdrop-blur-sm shadow-lg lg:px-2 lg:py-1 lg:bg-opacity-90">
        <img src={thiranLogo} alt="Thiran Tech Logo" className="w-36" />
      </div>

      {/* Form Container */}
      <div
        className="
          max-w-[600px]
          p-8
          bg-white
          rounded-lg
          shadow-xl
          mt-[15px] mb-[10px] mx-[10px]
          lg:mt-0 lg:mb-[20px] lg:mx-auto
          border border-gray-100
        "
        style={{ fontFamily: "Poppins, sans-serif" }}
      >
        {submitted ? (
          <div className="text-center">
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: "#0057A7", fontFamily: "Poppins, sans-serif" }}
            >
              Thank you!
            </h2>
            <div
              style={{ fontFamily: "Poppins, sans-serif", color: "#333333" }}
            >
              You'll receive your demo credentials via email.
              <br />
              {detectedPlatform === "android" && (
                <>
                  <div>
                    Redirecting to the <strong>Google Play Store</strong> in{" "}
                    <strong>{countdown}</strong>...
                  </div>
                  <div className="mt-2 text-sm">
                    If you're not redirected,{" "}
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: "#0057A7" }}
                    >
                      click here
                    </a>
                    .
                  </div>
                </>
              )}
              {detectedPlatform === "ios" && (
                <>
                  <div>
                    Redirecting to the <strong>App Store</strong> in{" "}
                    <strong>{countdown}</strong>...
                  </div>
                  <div className="mt-2 text-sm">
                    If you're not redirected,{" "}
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: "#0057A7" }}
                    >
                      click here
                    </a>
                    .
                  </div>
                </>
              )}
              {detectedPlatform === "web" && (
                <>
                  <>You can manually download the app below.</>
                  {renderStoreButtons()}
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <h2
              className="text-center font-bold mb-2 text-[20px] druk-cyr-font"
              style={{ color: "#0057A7" }}
            >
              Try T-EAM Mobile – See It. Tap It. Use It
            </h2>
            <p
              className="text-center mb-8"
              style={{ color: "#333333", fontFamily: "Poppins, sans-serif" }}
            >
              Enter your details to access the award-winning Mobile App for
              Maximo.
            </p>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                name="name"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Full Name
                  </span>
                }
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input
                  placeholder="e.g., Alice Johnson"
                  className="kiosk-input"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Email Address
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Invalid email address" },
                ]}
              >
                <Input
                  placeholder="e.g., alice@company.com"
                  className="kiosk-input"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="phone"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Phone Number
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your phone number" },
                ]}
              >
                <Input
                  placeholder="e.g., +1 555 123 4567"
                  className="kiosk-input"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="company"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Company Name
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your company" },
                ]}
              >
                <Input
                  placeholder="e.g., Acme Inc."
                  className="kiosk-input"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="jobTitle"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Job Title
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your job title" },
                ]}
              >
                <Input
                  placeholder="e.g., Head of Operations"
                  className="kiosk-input"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="industry"
                label={
                  <span
                    style={{
                      color: "#0057A7",
                      fontWeight: 700,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Industry
                  </span>
                }
                rules={[
                  { required: true, message: "Please select your industry" },
                ]}
              >
                <Select
                  placeholder="Select your industry"
                  className="kiosk-select"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                >
                  {/* Industries with dedicated demo users from CSV files */}
                  <Option value="Aviation">Aviation</Option>
                  <Option value="Facilities">Facilities Management</Option>
                  <Option value="Life Science">Life Sciences</Option>
                  <Option value="Transport">Transportation</Option>
                  <Option value="Utility">Utilities</Option>

                  {/* Fallback option for unlisted industries */}
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  className="mt-4 kiosk-nav-button-primary"
                  loading={isLoading}
                  style={{
                    height: "auto",
                    padding: "12px 24px",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  <p style={{ fontFamily: "Poppins, sans-serif" }}>
                    Get Demo Access
                  </p>
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </div>
    </div>
  );
};

export default DemoEnrollmentForm;
